import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { httpService } from '@/services/HttpService';
import type {
  UserNotification,
  UserNotificationListMeta,
  UserNotificationListResponse,
  UserNotificationPreferences,
  UserNotificationPreferencesResponse,
  UserNotificationStatsResponse,
} from '@/types';
import { getApiErrorMessage } from '@/utils/apiError';
import { useToastStore } from './toast';
import { useAuthStore } from './auth';

const DEFAULT_NOTIFICATION_EVENTS: Readonly<Record<string, boolean>> = {
  'follow.new': true,
};

const DEFAULT_PREFERENCES: UserNotificationPreferences = {
  email_enabled: true,
  in_app_enabled: true,
  events: { ...DEFAULT_NOTIFICATION_EVENTS },
};

const normalizePreferences = (
  value: Partial<UserNotificationPreferences> | null | undefined
): UserNotificationPreferences => {
  return {
    email_enabled: value?.email_enabled ?? true,
    in_app_enabled: value?.in_app_enabled ?? true,
    events: {
      ...DEFAULT_NOTIFICATION_EVENTS,
      ...(value?.events ?? {}),
    },
  };
};

const POLL_INTERVAL_DEFAULT_MS = 120_000; // 2 minutes (was 60_000)
const POLL_INTERVAL_MAX_MS = 600_000; // 10 minutes (backoff cap)
const LS_NEXT_POLL_KEY = 'tfs_notifications_next_poll';
const LS_UNREAD_COUNT_KEY = 'tfs_notifications_unread_count';
const LS_TOTAL_COUNT_KEY = 'tfs_notifications_total_count';

const shouldPollNow = (): boolean => {
  if (import.meta.env.SSR) return false;
  const next = localStorage.getItem(LS_NEXT_POLL_KEY);
  if (!next) return true;
  return Date.now() >= parseInt(next, 10);
};

const scheduleNextPoll = (): void => {
  if (import.meta.env.SSR) return;
  localStorage.setItem(LS_NEXT_POLL_KEY, String(Date.now() + POLL_INTERVAL_DEFAULT_MS));
};

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<UserNotification[]>([]);
  const unreadCount = ref(0);
  const totalCount = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const preferences = ref<UserNotificationPreferences>({ ...DEFAULT_PREFERENCES });
  const pagination = ref<UserNotificationListMeta>({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  let pollingTimer: ReturnType<typeof setInterval> | null = null;
  let currentIntervalMs = POLL_INTERVAL_DEFAULT_MS;
  let crossTabSyncActive = false;
  const toastStore = useToastStore();

  const recentItems = computed(() => items.value.slice(0, 10));

  const fetchNotifications = async (page = 1, perPage = 15): Promise<void> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<UserNotificationListResponse>(
        `/user/notifications?page=${page}&per_page=${perPage}`
      );

      items.value = response.data;
      pagination.value = response.meta;

      const unreadInPage = response.data.filter((item) => item.read_at === null).length;
      if (response.meta.current_page === 1 && response.meta.per_page >= response.meta.total) {
        unreadCount.value = unreadInPage;
      }
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to load notifications');
    } finally {
      loading.value = false;
    }
  };

  const fetchUnreadCount = async (): Promise<void> => {
    if (!useAuthStore().isAuthenticated || !shouldPollNow()) {
      return;
    }

    try {
      const response = await httpService.get<UserNotificationStatsResponse>('/user/notifications/stats');
      unreadCount.value = response.data.unread_count;
      totalCount.value = response.data.total_count;
      currentIntervalMs = POLL_INTERVAL_DEFAULT_MS;
      scheduleNextPoll();
      if (!import.meta.env.SSR) {
        localStorage.setItem(LS_UNREAD_COUNT_KEY, String(response.data.unread_count));
        localStorage.setItem(LS_TOTAL_COUNT_KEY, String(response.data.total_count));
      }
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Failed to load notification stats');
      currentIntervalMs = Math.min(currentIntervalMs * 2, POLL_INTERVAL_MAX_MS);
      restartTimer();
    }
  };

  const markAsRead = async (notificationId: string): Promise<boolean> => {
    const item = items.value.find((notification) => notification.id === notificationId);
    const wasUnread = item?.read_at === null;

    if (item && wasUnread) {
      item.read_at = new Date().toISOString();
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }

    try {
      await httpService.put(`/user/notifications/${notificationId}/read`);
      return true;
    } catch (err: unknown) {
      if (item && wasUnread) {
        item.read_at = null;
        unreadCount.value += 1;
      }
      toastStore.addToast(getApiErrorMessage(err, 'Failed to mark notification as read'), 'error');
      return false;
    }
  };

  const markAllAsRead = async (): Promise<void> => {
    try {
      await httpService.post('/user/notifications/mark-all-read');
      items.value = items.value.map((notification) => ({
        ...notification,
        read_at: notification.read_at ?? new Date().toISOString(),
      }));
      unreadCount.value = 0;
    } catch (err: unknown) {
      toastStore.addToast(getApiErrorMessage(err, 'Failed to mark all notifications as read'), 'error');
    }
  };

  const removeOne = async (notificationId: string): Promise<void> => {
    const item = items.value.find((notification) => notification.id === notificationId);

    try {
      await httpService.delete(`/user/notifications/${notificationId}`);
      items.value = items.value.filter((notification) => notification.id !== notificationId);

      if (item?.read_at === null) {
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }

      totalCount.value = Math.max(0, totalCount.value - 1);
      pagination.value.total = Math.max(0, pagination.value.total - 1);
    } catch (err: unknown) {
      toastStore.addToast(getApiErrorMessage(err, 'Failed to delete notification'), 'error');
    }
  };

  const clearAll = async (): Promise<void> => {
    try {
      await httpService.delete('/user/notifications/clear-all');
      items.value = [];
      unreadCount.value = 0;
      totalCount.value = 0;
      pagination.value = {
        current_page: 1,
        per_page: pagination.value.per_page,
        total: 0,
        last_page: 1,
      };
    } catch (err: unknown) {
      toastStore.addToast(getApiErrorMessage(err, 'Failed to clear notifications'), 'error');
    }
  };

  const fetchPreferences = async (): Promise<void> => {
    try {
      const response = await httpService.get<UserNotificationPreferencesResponse>('/user/notifications/preferences');
      preferences.value = normalizePreferences(response.data);
    } catch (err: unknown) {
      toastStore.addToast(getApiErrorMessage(err, 'Failed to load notification preferences'), 'error');
    }
  };

  const savePreferences = async (payload: Partial<UserNotificationPreferences>): Promise<boolean> => {
    try {
      const response = await httpService.put<UserNotificationPreferencesResponse>('/user/notifications/preferences', payload);
      preferences.value = normalizePreferences(response.data);
      toastStore.addToast('Notification preferences updated');
      return true;
    } catch (err: unknown) {
      toastStore.addToast(getApiErrorMessage(err, 'Failed to save notification preferences'), 'error');
      return false;
    }
  };

  const restartTimer = (): void => {
    if (pollingTimer) {
      clearInterval(pollingTimer);
      pollingTimer = null;
    }

    pollingTimer = setInterval(() => {
      void fetchUnreadCount();
    }, currentIntervalMs);
  };

  const syncFromStorage = (): void => {
    if (import.meta.env.SSR) return;
    const unread = localStorage.getItem(LS_UNREAD_COUNT_KEY);
    const total = localStorage.getItem(LS_TOTAL_COUNT_KEY);
    if (unread !== null) unreadCount.value = parseInt(unread, 10);
    if (total !== null) totalCount.value = parseInt(total, 10);
  };

  const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key === LS_UNREAD_COUNT_KEY || event.key === LS_TOTAL_COUNT_KEY) {
      syncFromStorage();
    }
  };

  const setupCrossTabSync = (): void => {
    if (import.meta.env.SSR) return;
    if (crossTabSyncActive) return;
    window.addEventListener('storage', handleStorageEvent);
    crossTabSyncActive = true;
  };

  const teardownCrossTabSync = (): void => {
    if (import.meta.env.SSR) return;
    if (!crossTabSyncActive) return;
    window.removeEventListener('storage', handleStorageEvent);
    crossTabSyncActive = false;
  };

  const startPolling = (intervalMs = POLL_INTERVAL_DEFAULT_MS): void => {
    if (pollingTimer) {
      return;
    }

    currentIntervalMs = intervalMs;
    setupCrossTabSync();

    pollingTimer = setInterval(() => {
      void fetchUnreadCount();
    }, intervalMs);
  };

  const stopPolling = (): void => {
    if (!pollingTimer) {
      return;
    }

    clearInterval(pollingTimer);
    pollingTimer = null;
    teardownCrossTabSync();
  };

  const pausePolling = (): void => {
    if (!pollingTimer) {
      return;
    }

    clearInterval(pollingTimer);
    pollingTimer = null;
  };

  const resumePolling = (): void => {
    if (!useAuthStore().isAuthenticated) {
      return;
    }

    restartTimer();
    void fetchUnreadCount();
  };

  const reset = (): void => {
    stopPolling();
    currentIntervalMs = POLL_INTERVAL_DEFAULT_MS;
    if (!import.meta.env.SSR) {
      localStorage.removeItem(LS_UNREAD_COUNT_KEY);
      localStorage.removeItem(LS_TOTAL_COUNT_KEY);
      localStorage.removeItem(LS_NEXT_POLL_KEY);
    }
    items.value = [];
    unreadCount.value = 0;
    totalCount.value = 0;
    loading.value = false;
    error.value = null;
    preferences.value = { ...DEFAULT_PREFERENCES };
    pagination.value = {
      current_page: 1,
      per_page: 15,
      total: 0,
      last_page: 1,
    };
  };

  return {
    items,
    unreadCount,
    totalCount,
    loading,
    error,
    preferences,
    pagination,
    recentItems,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    removeOne,
    clearAll,
    fetchPreferences,
    savePreferences,
    startPolling,
    stopPolling,
    pausePolling,
    resumePolling,
    reset,
  };
});
